import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  X,
  User,
  Building2,
  Bell,
  Radio,
  Mail,
  Loader2,
  Linkedin,
  Handshake,
  Download,
  Users,
} from 'lucide-react';
import { EXTERNAL_LINKS } from '../../constants/links';
import { supabase } from '../../lib/supabase';
import { apiClient } from '../../lib/api';
import skylarqLogo from '../../assets/skylarq_logo_small.png';
import { useAuth } from '../../contexts/AuthContext';
import { UserProfile, IdealCustomerProfile } from '../../types';
import { useToast } from '../../hooks/useToast';
import { AccountSection } from './modal/AccountSection';
import { MembersSection } from './modal/MembersSection';
import { CompanySection } from './modal/CompanySection';
import { EmailSection } from './modal/EmailSection';
import { LinkedInSection } from './modal/LinkedInSection';
import { MonitorsSection } from './modal/MonitorsSection';
import { NotificationsSection } from './modal/NotificationsSection';
import { AffiliateSection } from './modal/AffiliateSection';
import { PartnerDashboard } from '../../types/affiliate';

export interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSection?: string;
  initialMonitorId?: string | null;
  onSectionChange?: (section: string) => void;
}

type NavSection = 'account' | 'members' | 'company' | 'email' | 'linkedin' | 'monitors' | 'notifications' | 'affiliate';

const baseNavItems: { id: NavSection; label: string; icon: React.ElementType }[] = [
  { id: 'account', label: 'Account', icon: User },
  { id: 'members', label: 'Members', icon: Users },
  { id: 'company', label: 'Company', icon: Building2 },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { id: 'monitors', label: 'Monitors', icon: Radio },
  { id: 'notifications', label: 'Notifications', icon: Bell },
];

const isNavSection = (section: string): section is NavSection =>
  section === 'affiliate' || baseNavItems.some((item) => item.id === section);

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  initialSection = 'account',
  initialMonitorId,
  onSectionChange,
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [activeSection, setActiveSection] = useState<NavSection>(initialSection as NavSection);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [icpProfiles, setIcpProfiles] = useState<IdealCustomerProfile[]>([]);
  const [editingIcp, setEditingIcp] = useState<IdealCustomerProfile | null>(null);
  const [showNewIcpForm, setShowNewIcpForm] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [partnerDashboard, setPartnerDashboard] = useState<PartnerDashboard | null>(null);

  const navItems = useMemo(() => {
    const items = [...baseNavItems];
    if (partnerDashboard?.is_partner) {
      items.push({ id: 'affiliate', label: 'Affiliate', icon: Handshake });
    }
    return items;
  }, [partnerDashboard]);

  const handleSectionChange = useCallback((section: NavSection) => {
    setActiveSection(section);
    onSectionChange?.(section);
  }, [onSectionChange]);

  const prevIsOpenRef = useRef(false);
  const hasAppliedAffiliate = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (!isOpen) {
      hasAppliedAffiliate.current = false;
      return;
    }

    if (justOpened) {
      if (!isNavSection(initialSection)) {
        handleSectionChange('account');
      } else if (initialSection === 'affiliate' && !partnerDashboard?.is_partner) {
        // Partner data may not be loaded yet — fall back for now
        setActiveSection('account');
      } else {
        setActiveSection(initialSection);
      }
    } else if (
      initialSection === 'affiliate' &&
      partnerDashboard?.is_partner &&
      !hasAppliedAffiliate.current
    ) {
      // Partner data arrived after open — apply the deferred affiliate tab
      hasAppliedAffiliate.current = true;
      setActiveSection('affiliate');
    }
  }, [isOpen, initialSection, partnerDashboard, handleSectionChange]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const loadUserData = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const [profileResult, icpResult, partnerResult] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('ideal_customer_profiles')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: true }),
        apiClient.getMyPartnerDashboard().catch(() => ({ is_partner: false }) as PartnerDashboard),
      ]);

      setProfile(profileResult.data);
      setIcpProfiles(icpResult.data || []);
      setPartnerDashboard(partnerResult);
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen && user) {
      loadUserData();
    }
  }, [isOpen, user, loadUserData]);

  const handleSaveProfile = useCallback(async (updatedProfile?: UserProfile | null) => {
    const profileToSave = updatedProfile || profile;
    if (!user || !profileToSave) return false;

    setIsSaving(true);
    try {
      const data = await apiClient.updateUserProfile({
        company_website: profileToSave.company_website
      });

      setProfile((prev) => (prev ? { ...prev, ...data } : data));
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      return true;
    } catch {
      showToast('Failed to save profile. Please try again.', 'error');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [user, profile, showToast]);

  const handleSaveIcp = useCallback(async (icp: Partial<IdealCustomerProfile>) => {
    if (!user) return;
    setIsSaving(true);
    try {
      const fields = {
        name: icp.name,
        solution_products: icp.solution_products || '',
        target_region: icp.target_region ?? [],
        target_customers: icp.target_customers || '',
      };
      if (icp.id) {
        await apiClient.updateICPProfile(icp.id, fields);
      } else {
        await apiClient.createICPProfile(fields);
      }
      await loadUserData();
      setEditingIcp(null);
      setShowNewIcpForm(false);
    } catch {
      showToast('Failed to save ICP. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [user, loadUserData, showToast]);

  const handleDeleteIcp = useCallback(async (icpId: string) => {
    try {
      await apiClient.deleteICPProfile(icpId);
      await loadUserData();
    } catch {
      showToast('Failed to delete ICP. Please try again.', 'error');
    }
  }, [loadUserData, showToast]);

  if (!isOpen) return null;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      );
    }

    switch (activeSection) {
      case 'account':
        return <AccountSection onClose={onClose} />;
      case 'members':
        return <MembersSection />;
      case 'company':
        return (
          <CompanySection
            profile={profile}
            icpProfiles={icpProfiles}
            editingIcp={editingIcp}
            showNewIcpForm={showNewIcpForm}
            showSuccessMessage={showSuccessMessage}
            isSaving={isSaving}
            onProfileUpdate={setProfile}
            onSaveProfile={handleSaveProfile}
            onSaveIcp={handleSaveIcp}
            onDeleteIcp={handleDeleteIcp}
            onEditIcp={setEditingIcp}
            onShowNewIcpForm={setShowNewIcpForm}
          />
        );
      case 'email':
        return <EmailSection />;
      case 'linkedin':
        return <LinkedInSection />;
      case 'monitors':
        return <MonitorsSection initialMonitorId={initialMonitorId} />;
      case 'notifications':
        return <NotificationsSection />;
      case 'affiliate':
        return partnerDashboard?.is_partner ? <AffiliateSection dashboard={partnerDashboard} /> : null;
      default:
        return <AccountSection onClose={onClose} />;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] md:flex md:items-center md:justify-center animate-fadeIn bg-black/40"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="flex flex-col md:flex-row bg-white md:rounded-2xl shadow-modal overflow-hidden animate-fadeInScale w-full md:max-w-3xl h-full md:h-[600px]">
        <div className="flex-shrink-0 flex flex-col bg-neutral-50 border-b md:border-b-0 md:border-r border-neutral-100 md:w-[200px]">
          <div className="p-4 flex items-center justify-between gap-2.5 border-b border-neutral-100 mt-2">
            <div className="flex items-center gap-2.5">
              <img src={skylarqLogo} alt="Skylarq" className="w-6 h-6" />
              <span className="text-[14px] font-semibold text-neutral-900">Settings</span>
            </div>
            <button
              onClick={onClose}
              className="md:hidden p-1.5 rounded-lg transition-colors hover:bg-neutral-100 text-neutral-400"
              aria-label="Close settings"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex md:flex-col md:flex-1 px-4 py-3 md:p-2 overflow-x-auto md:overflow-x-visible gap-2 md:gap-0">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleSectionChange(item.id)}
                  className={`flex-shrink-0 md:w-full flex items-center gap-2 px-3 py-2 rounded-full md:rounded-lg text-left text-[13px] font-medium transition-all duration-150 md:mb-0.5 whitespace-nowrap border md:border-0 ${
                    isActive 
                      ? 'bg-neutral-900 text-white border-neutral-900 md:bg-white md:text-neutral-900 md:shadow-sm' 
                      : 'bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50 md:bg-transparent md:hover:bg-white/60 md:text-neutral-500'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </button>
              );
            })}
          </nav>

          {!profile?.onboarding_completed && (
            <div className="hidden md:block p-3 border-t border-neutral-100">
              <button
                id="settings-desktop-download"
                type="button"
                onClick={() => window.open(EXTERNAL_LINKS.DESKTOP_DOWNLOAD, '_blank', 'noopener,noreferrer')}
                aria-label="Download the Skylarq desktop app"
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-30"
              >
                <Download className="w-3.5 h-3.5" />
                Get desktop app
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="hidden md:flex items-center justify-between px-6 py-4 border-b border-neutral-100">
            <h2 className="text-[15px] font-semibold text-neutral-900">
              {navItems.find(item => item.id === activeSection)?.label || 'Account'}
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg transition-colors hover:bg-neutral-100 text-neutral-400"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 md:p-6 pb-8">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};
